import { forwardRef, useImperativeHandle, useState } from 'react';
import type { User } from '../../types/comments';

export interface MentionListProps {
  items: User[];
  command: (props: { id: number; label: string }) => void;
}

export interface MentionListHandle {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

export const MentionList = forwardRef<MentionListHandle, MentionListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = (index: number) => {
    const item = props.items[index];
    if (item) {
      props.command({ id: item.id, label: item.fullName || item.email || 'User' });
    }
  };

  const upHandler = () => {
    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % props.items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }
      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }
      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }
      return false;
    },
  }));

  return (
    <div className="bg-background border border-border rounded-md shadow-lg overflow-hidden min-w-[150px] flex flex-col p-1">
      {props.items.length ? (
        props.items.map((item, index) => (
          <button
            key={item.id}
            className={`flex flex-col items-start w-full px-3 py-1.5 text-sm rounded-sm transition-colors text-left ${
              index === selectedIndex ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
            }`}
            onClick={() => selectItem(index)}
          >
            <span className="font-medium">{item.fullName || 'Unnamed User'}</span>
            <span
              className={`text-[10px] ${index === selectedIndex ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}
            >
              {item.email}
            </span>
          </button>
        ))
      ) : (
        <div className="px-3 py-2 text-sm text-muted-foreground italic">No users found</div>
      )}
    </div>
  );
});

MentionList.displayName = 'MentionList';
